import React, { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { FieldProps } from 'formik'
import _ from 'lodash'
import cn from 'classnames'
import { Box, Grid, TextField as MuiTextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import { importPlaces, isGoogleMapsAvailable } from 'civic-champs-shared/core/google-maps/loader'

import CompactGeofenceRadiusSlider from 'civic-champs-shared/core/CompactGeofenceRadiusSlider'

import { DEFAULT_GEOFENCING } from '../../constants/GEO_DATA'
import { MAX_RADIUS_IN_METER, MIN_RADIUS_IN_METER } from '../../helpers/toRoundMetersTo5Feet'
import GeofenceRadiusSlider from '../../core/geofence-radius-slider'
import ReactGoogleMap, { MAP } from '../../core/location/ReactGoogleMap'
import { Geofencing } from '../../core/location/utils'

import { PlacePredictionOption } from './PlacePredictionOption'
import { inputLooksLikePlusCode, resolvePlusCodeToCoordinates } from '../../core/location/plus-code-utils'

export const defaultAddress: () => Address = () => ({
  description: '',
  city: '',
  state: '',
  zip: '',
  line1: '',
  line2: '',
  geofencing: DEFAULT_GEOFENCING,
  country: '',
  county: '',
  utcOffset: 0,
})

export interface Address {
  description?: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
  geofencing: Geofencing
  country: string
  county: string
  countryCode?: string
  utcOffset: number
}

export interface AddressFieldProps extends FieldProps {
  showRadius?: boolean
  fieldTitle?: string
  label?: string
  hideMap?: boolean
  placeholder?: string
  loadLocationFromAddress?: boolean
  vertical?: boolean
  halfMap?: boolean
  smaller?: boolean
  labelShrink?: boolean
  compactSlider?: boolean
  autocompleteDisabled?: boolean
  className?: string
  allowPlusCode?: boolean
  referenceCoordinates?: { lat: number; lng: number }
}

// Initialize Google Maps services using shared loader
const googleService: {
  autoComplete?: google.maps.places.AutocompleteService
  places?: google.maps.places.PlacesService
} = {}

export type GoogleAutoCompleteMixedResponse = google.maps.places.AutocompletePrediction

const useStyles = makeStyles({
  verticalGap: {
    gap: '10px',
  },
  smaller: {
    padding: '3px 9px 2px 0px !important',
  },
  smallerInput: {
    padding: ' 8.5px 9px',
  },
  focused: {
    '& > fieldset': {
      borderColor: 'inherit !important',
      borderWidth: '1px !important',
    },
  },
  inputFocused: {
    color: 'inherit !important',
  },
})

const getAddressString = (address: Address) => {
  const { line1, line2, city, county, state, zip } = address
  return [line1, line2, city, county, state, zip].filter(value => !!value).join(', ')
}

const useAddressFieldPlusCode = ({
  enabled,
  referenceCoordinates,
  name,
  value,
  geofencing,
  setFieldValue,
  setTouched,
  touched,
}: {
  enabled: boolean
  referenceCoordinates?: { lat: number; lng: number }
  name: string
  value: Address
  geofencing: Geofencing
  setFieldValue: FieldProps['form']['setFieldValue']
  setTouched: FieldProps['form']['setTouched']
  touched: FieldProps['form']['touched']
}) => {
  const [plusCodeError, setPlusCodeError] = useState<string | null>(null)
  const [isResolvingPlusCode, setIsResolvingPlusCode] = useState(false)
  const resolveRequestId = useRef(0)
  const descriptionInputRef = useRef('')

  const resolvePlusCodeInput = useCallback(
    async (inputValue: string) => {
      const trimmed = inputValue.trim()
      if (!enabled || !trimmed || !inputLooksLikePlusCode(trimmed)) {
        return
      }

      const requestId = ++resolveRequestId.current
      setIsResolvingPlusCode(true)
      setPlusCodeError(null)

      const result = await resolvePlusCodeToCoordinates({
        input: trimmed,
        referenceCoordinates,
      })

      if (requestId !== resolveRequestId.current) {
        return
      }

      setIsResolvingPlusCode(false)

      if ('error' in result) {
        setPlusCodeError(result.error)
        return
      }

      const { city, state, zip, county, country, countryCode } = result.address
      setFieldValue(name, {
        ...value,
        description: trimmed,
        line1: trimmed,
        city,
        state,
        zip,
        county,
        country,
        countryCode,
        geofencing: {
          ...geofencing,
          location: {
            type: 'Point' as const,
            coordinates: {
              lat: result.lat,
              lng: result.lng,
            },
          },
        },
      })
      setPlusCodeError(null)
    },
    [enabled, geofencing, name, referenceCoordinates, setFieldValue, value],
  )

  const applyPlusCodeInputChange = useCallback(
    (input: string) => {
      if (!enabled || !inputLooksLikePlusCode(input)) {
        return false
      }

      setPlusCodeError(null)
      if (!touched[name]) {
        setTouched({ [name]: true })
      }

      setFieldValue(name, {
        ...value,
        description: input,
        line1: input,
        city: '',
        state: '',
        zip: '',
        country: '',
        county: '',
        countryCode: '',
      })
      return true
    },
    [enabled, name, setFieldValue, setTouched, touched, value],
  )

  const shouldSkipPlacePredictions = useCallback(
    (input: string) => enabled && inputLooksLikePlusCode(input),
    [enabled],
  )

  const scheduleResolveFromInputRef = useCallback(() => {
    window.setTimeout(() => {
      void resolvePlusCodeInput(descriptionInputRef.current)
    }, 0)
  }, [resolvePlusCodeInput])

  return {
    plusCodeError,
    isResolvingPlusCode,
    descriptionInputRef,
    resolvePlusCodeInput,
    applyPlusCodeInputChange,
    shouldSkipPlacePredictions,
    scheduleResolveFromInputRef,
    clearPlusCodeError: () => setPlusCodeError(null),
  }
}

export const AddressField = (props: AddressFieldProps) => {
  const {
    form: { setFieldValue, errors, touched, setTouched },
    field: { name, value },
    showRadius,
    fieldTitle,
    label,
    hideMap,
    placeholder,
    loadLocationFromAddress,
    vertical,
    smaller,
    labelShrink,
    halfMap,
    compactSlider,
    autocompleteDisabled,
    className,
    allowPlusCode = false,
    referenceCoordinates,
  } = props
  const addressError = _.get(touched, name) && _.get(errors, name)
  const [line1Options, setLine1Options] = useState<GoogleAutoCompleteMixedResponse[]>([])
  const [optionsFetching, setOptionsFetching] = useState<boolean>(false)
  const [enterPressed, setEnterPressed] = useState<boolean>(false)
  const [selectFirstOptionDelayed, setSelectFirstOptionDelayed] = useState<boolean>(false)
  const { geofencing = DEFAULT_GEOFENCING } = value || {}
  const [mapsLoaded, setMapsLoaded] = useState(false)
  const {
    plusCodeError,
    isResolvingPlusCode,
    descriptionInputRef,
    resolvePlusCodeInput,
    applyPlusCodeInputChange,
    shouldSkipPlacePredictions,
    scheduleResolveFromInputRef,
    clearPlusCodeError,
  } = useAddressFieldPlusCode({
    enabled: allowPlusCode,
    referenceCoordinates,
    name,
    value: value || defaultAddress(),
    geofencing,
    setFieldValue,
    setTouched,
    touched,
  })

  // Load Google Maps API when component mounts
  useEffect(() => {
    const loadMapsAPI = async () => {
      try {
        // Use shared loader to load Places library
        await importPlaces()

        // Initialize AutocompleteService if not already initialized
        if (!googleService.autoComplete && isGoogleMapsAvailable()) {
          googleService.autoComplete = new window.google.maps.places.AutocompleteService()
        }

        setMapsLoaded(true)
      } catch (error) {
        console.error('Error loading Google Maps API:', error)
      }
    }

    loadMapsAPI()
  }, [hideMap])

  const getDetails = React.useMemo(
    () =>
      _.throttle(
        (
          request: { placeId: string },
          callback: (result: google.maps.places.PlaceResult, status: google.maps.places.PlacesServiceStatus) => void,
        ) => {
          // Skip if Maps API is not loaded or if places service is not available
          if (!mapsLoaded || !googleService.places) {
            console.log('Google Maps not loaded yet or places service not available')
            return
          }

          googleService.places.getDetails(request, callback)
        },
        500,
      ),
    [mapsLoaded],
  )

  const handleChangeRadius = useCallback(
    (newRadius: number) => {
      setFieldValue(name, {
        ...value,
        geofencing: {
          ...geofencing,
          radius: newRadius,
        },
      })
    },
    [geofencing, name, setFieldValue, value],
  )

  const handleOptionSelect = useCallback(
    (e: React.ChangeEvent<{}> | null, v: GoogleAutoCompleteMixedResponse) => {
      if (_.isEmpty(v) || !v.place_id) {
        return
      }
      getDetails({ placeId: v.place_id }, place => {
        const address = parsePlace(place, value?.geofencing?.radius)
        const { state, zip, city, geofencing, country, countryCode, county, line1 } = address
        const newValue = {
          ...value,
          description: getAddressString(address),
          line1,
          city,
          zip,
          state,
          geofencing,
          country,
          county,
          countryCode,
        }
        setFieldValue(name, {
          ...newValue,
          geofencing: {
            ...geofencing,
            radius: geofencing.radius + 1,
          },
        })
        // hack to make sure that map is centered properly after being hidden
        window.requestAnimationFrame(() => {
          setFieldValue(name, newValue)
        })
      })
    },
    [getDetails, name, setFieldValue, value],
  )

  const selectFirstOption = useCallback(() => {
    if (optionsFetching) {
      return setSelectFirstOptionDelayed(true)
    }
    setSelectFirstOptionDelayed(false)
    if (line1Options.length) {
      handleOptionSelect(null, line1Options[0])
    }
  }, [handleOptionSelect, line1Options, optionsFetching])

  const getPlacePredictions = React.useMemo(
    () =>
      _.debounce((request: { input: string }, callback: (input: GoogleAutoCompleteMixedResponse[]) => void) => {
        // Don't attempt to get predictions if maps aren't loaded
        if (!mapsLoaded) {
          console.log('Google Maps not loaded yet, skipping predictions')
          setOptionsFetching(false)
          return
        }

        // Optimization: Don't make API calls for very short inputs
        if (request.input.length < 3) {
          callback([])
          setOptionsFetching(false)
          return
        }

        if (shouldSkipPlacePredictions(request.input)) {
          callback([])
          setOptionsFetching(false)
          return
        }

        setOptionsFetching(true)

        // Optimization: Only use getPlacePredictions (removed expensive findPlaceFromQuery)
        if (googleService.autoComplete) {
          googleService.autoComplete.getPlacePredictions(
            {
              ...request,
              types: ['address'],
              componentRestrictions: { country: ['us', 'ca'] },
            },
            (result: google.maps.places.AutocompletePrediction[]) => {
              callback(result || [])
              setOptionsFetching(false)
              if (selectFirstOptionDelayed) {
                selectFirstOption()
              }
            },
          )
        } else {
          callback([])
          setOptionsFetching(false)
        }
      }, Number(process.env.REACT_APP_PLACES_DEBOUNCE_DELAY) || 1000), // Configurable delay for cost optimization
    [mapsLoaded, selectFirstOption, selectFirstOptionDelayed, shouldSkipPlacePredictions],
  )

  const onChange = (fieldName: string, fieldValue: string) => {
    !touched[name] && setTouched({ [name]: true })
    setFieldValue(name, {
      ...value,
      [fieldName]: fieldValue,
    })
  }

  useEffect(() => {
    if (value && value.line1 && !value.description) {
      onChange('description', getAddressString(value))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onMapMounted = async (map: any) => {
    if (_.isEmpty(map)) {
      return
    }
    // Check if the map is already initialized
    if (googleService.places) {
      return
    }

    const mapObject = map.context[MAP]

    try {
      // Ensure Places library is loaded
      await importPlaces()

      // Only initialize PlacesService with map when it's not already initialized
      if (mapObject && isGoogleMapsAvailable()) {
        googleService.places = new window.google.maps.places.PlacesService(mapObject)
      }
    } catch (error) {
      console.error('Error loading Places library:', error)
    }
  }

  useEffect(() => {
    let timeoutId: number
    if (loadLocationFromAddress) {
      timeoutId = setTimeout(
        () =>
          getPlacePredictions({ input: value.description }, (results: GoogleAutoCompleteMixedResponse[]) => {
            results.length && handleOptionSelect(null, _.first(results) as any)
          }),
        1000,
      )
    }
    return () => clearTimeout(timeoutId)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles()
  const inputValue = _.get(value, 'description', '')
  descriptionInputRef.current = inputValue

  return (
    <Grid
      container
      direction={vertical ? 'column-reverse' : 'row'}
      className={cn(className, { [classes.verticalGap]: vertical })}
    >
      {/*
        We need map reference to have working search.
        So we hide block instead of conditional rendering
      */}
      <Grid item sm={vertical && !halfMap ? 12 : 6} style={{ display: hideMap ? 'none' : 'block' }}>
        <Box pr={vertical ? 0 : 2}>
          <ReactGoogleMap
            coordinates={_.get(value, 'geofencing.location.coordinates')}
            geofenceRadius={geofencing.radius}
            onMounted={onMapMounted}
          />
          {showRadius &&
            (compactSlider ? (
              <CompactGeofenceRadiusSlider
                radius={geofencing.radius}
                min={MIN_RADIUS_IN_METER}
                max={MAX_RADIUS_IN_METER}
                onChange={handleChangeRadius}
              />
            ) : (
              <GeofenceRadiusSlider
                radius={geofencing.radius}
                min={MIN_RADIUS_IN_METER}
                max={MAX_RADIUS_IN_METER}
                onChange={handleChangeRadius}
              />
            ))}
        </Box>
      </Grid>
      <Grid container item sm={hideMap || vertical ? 12 : 6}>
        <Grid item sm={12} xs={12}>
          {fieldTitle && <h3 className="location-form__title">{fieldTitle}</h3>}
          <Autocomplete
            value={inputValue}
            disabled={autocompleteDisabled}
            // @ts-ignore
            onChange={handleOptionSelect}
            onInputChange={(e, v) => {
              descriptionInputRef.current = v
              clearPlusCodeError()

              if (applyPlusCodeInputChange(v)) {
                setLine1Options([])
                return
              }

              onChange('description', v)
              if (_.isEmpty(v) || v.length < 3) {
                setLine1Options([])
                return
              }
              getPlacePredictions({ input: v }, (results: GoogleAutoCompleteMixedResponse[]) => {
                setLine1Options(results || [])
              })
            }}
            options={line1Options}
            getOptionLabel={option => {
              if (typeof option === 'string') {
                return option
              }
              // After optimization: option is always AutocompletePrediction with description
              return option.description
            }}
            renderInput={params => {
              const formError = _.isString(addressError)
                ? addressError
                : _.get(addressError, 'description') ?? _.get(addressError, 'line1')
              const e = plusCodeError || formError
              const helperText =
                isResolvingPlusCode && !plusCodeError && !formError ? 'Resolving Plus Code…' : e
              return (
                <MuiTextField
                  {...params}
                  label={label}
                  variant="outlined"
                  error={!!e}
                  helperText={helperText}
                  placeholder={placeholder}
                  fullWidth
                  // This is a hack to disable chrome auto-fill:
                  // https://stackoverflow.com/questions/15738259/disabling-chrome-autofill
                  inputProps={{ ...params.inputProps, value: inputValue, autoComplete: 'new-password' }}
                  InputProps={
                    smaller
                      ? {
                          ...params.InputProps,
                          classes: {
                            root: classes.smaller,
                            input: classes.smallerInput,
                            focused: classes.focused,
                          },
                        }
                      : {
                          ...params.InputProps,
                          classes: {
                            focused: classes.focused,
                          },
                        }
                  }
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: labelShrink,
                    classes: {
                      focused: classes.inputFocused,
                    },
                  }}
                  onPaste={() => {
                    if (allowPlusCode) {
                      scheduleResolveFromInputRef()
                    }
                  }}
                  onKeyPress={(event: KeyboardEvent<HTMLDivElement>) => {
                    event.stopPropagation()
                    if (event.key === 'Enter') {
                      const currentInput = descriptionInputRef.current || inputValue
                      if (shouldSkipPlacePredictions(currentInput)) {
                        setEnterPressed(true)
                        void resolvePlusCodeInput(currentInput)
                        // @ts-ignore
                        event.target.blur()
                        return
                      }
                      setEnterPressed(true)
                      selectFirstOption()
                      // @ts-ignore
                      event.target.blur()
                    }
                  }}
                  onBlur={() => {
                    if (enterPressed) {
                      setEnterPressed(false)
                      return
                    }
                    const currentInput = descriptionInputRef.current || inputValue
                    if (shouldSkipPlacePredictions(currentInput)) {
                      void resolvePlusCodeInput(currentInput)
                      return
                    }
                    selectFirstOption()
                  }}
                />
              )
            }}
            renderOption={PlacePredictionOption}
            freeSolo={true}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export const parsePlace = (place: google.maps.places.PlaceResult, radius?: number): Address => {
  const addressComponent = new Map<string, string>()

  const processAddressComponents = (fieldName: 'long_name' | 'short_name') => {
    _.each(place.address_components, (c: google.maps.GeocoderAddressComponent) => {
      // Set all types for this component, not just the first one
      _.each(c.types, (type: string) => {
        addressComponent.set(type, c[fieldName])
      })
    })
  }

  // Process long names first
  processAddressComponents('long_name')

  const address = {
    city:
      addressComponent.get('locality') ||
      addressComponent.get('sublocality') ||
      addressComponent.get('sublocality_level_1') ||
      '',
    zip: addressComponent.get('postal_code') || '',
    state: addressComponent.get('administrative_area_level_1') || '',
    country: addressComponent.get('country') || '',
    county: addressComponent.get('administrative_area_level_2') || '',
    geofencing: {
      ...(!place.geometry
        ? defaultAddress().geofencing
        : {
            location: {
              type: 'Point',
              coordinates: place.geometry.location.toJSON(),
            },
          }),
      ...(radius ? { radius } : {}),
    },
    utcOffset: place.utc_offset || 0,
  } as Address

  // Process short names
  processAddressComponents('short_name')

  let line1 = addressComponent.get('street_number') || ''
  line1 += line1.length ? ' ' : ''
  line1 += addressComponent.get('route') || ''

  return {
    ...address,
    line1,
    countryCode: addressComponent.get('country') || '',
  }
}
